import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
const CART_ICON =
  '<svg class="livani-upsell__cart-icon" viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="20" r="1.4"/><circle cx="18" cy="20" r="1.4"/><path d="M2.5 3h2.6l2.4 11.4a1.6 1.6 0 0 0 1.6 1.3h8.6a1.6 1.6 0 0 0 1.6-1.2L21 7H6.2"/></svg><span class="livani-upsell__spinner" aria-hidden="true"></span>';

function money(cents) {
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents) / 100);
  } catch (e) {
    return '$' + (Number(cents) / 100).toFixed(2);
  }
}

function esc(value) {
  const div = document.createElement('div');
  div.textContent = value == null ? '' : String(value);
  return div.innerHTML.replace(/"/g, '&quot;');
}

function sized(src, width) {
  if (!src) return '';
  const url = src.startsWith('//') ? 'https:' + src : src;
  return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
}

/**
 * "Others Also Bought" carousel in the cart drawer.
 * Server-rendered slides come from Liquid; if none, it loads Shopify's
 * product recommendations for the first cart item.
 */
class LivaniCartUpsell extends HTMLElement {
  connectedCallback() {
    if (!this._bound) {
      this._bound = true;
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('scroll', this.onScroll, true);
      this._observer = new MutationObserver(() => this.scheduleEnsure());
      this._observer.observe(this, { attributes: true, attributeFilter: ['data-exclude', 'data-server-count'], childList: true, subtree: true });
    }
    this.scheduleEnsure();
  }

  disconnectedCallback() {
    clearTimeout(this._ensureTimer);
  }

  scheduleEnsure() {
    clearTimeout(this._ensureTimer);
    this._ensureTimer = setTimeout(() => this.ensure(), 60);
  }

  get track() {
    return this.querySelector('[data-upsell-track]');
  }

  get slides() {
    return Array.from(this.querySelectorAll('[data-upsell-slide]'));
  }

  async ensure() {
    const track = this.track;
    if (!track) return;

    const exclude = this.dataset.exclude || ',';
    const serverCount = Number(this.dataset.serverCount || 0);

    if (serverCount > 0) {
      this.hidden = false;
      this.syncControls();
      return;
    }

    // Already filled client-side for this exact cart.
    if (this.slides.length && this._filledFor === exclude) {
      this.hidden = false;
      return;
    }

    if (this._loading === exclude) return;
    this._loading = exclude;

    const products = await this.fetchProducts(exclude);
    if (this._loading !== exclude) return;
    this._loading = null;

    if (!products.length) {
      this.hidden = true;
      return;
    }

    this._filledFor = exclude;
    track.innerHTML = products.map((p, i) => this.slideHtml(p, i + 1)).join('');
    this.hidden = false;
    this.syncControls();
  }

  async fetchProducts(exclude) {
    const productId = this.dataset.productId;
    const isAllowed = (p) => p && p.available && !exclude.includes(',' + p.id + ',');
    let list = [];

    if (productId) {
      try {
        const res = await fetch(`${ROOT}recommendations/products.json?product_id=${productId}&limit=10&intent=related`);
        if (res.ok) list = ((await res.json()).products || []).filter(isAllowed);
      } catch (e) {
        /* ignore */
      }
    }

    if (list.length < 3) {
      try {
        const res = await fetch(`${ROOT}collections/all/products.json?limit=20`);
        if (res.ok) {
          const more = ((await res.json()).products || []).filter((p) => {
            p.available = p.available ?? (p.variants || []).some((v) => v.available);
            return isAllowed(p);
          });
          const ids = new Set(list.map((p) => p.id));
          list = list.concat(more.filter((p) => !ids.has(p.id)));
        }
      } catch (e) {
        /* ignore */
      }
    }

    return list.slice(0, 6);
  }

  slideHtml(p, index) {
    const variants = (p.variants || []).filter((v) => v.available !== false);
    const v = variants[0] || (p.variants || [])[0] || {};
    const toCents = (value) => (typeof value === 'string' && value.includes('.') ? Math.round(parseFloat(value) * 100) : Number(value));
    const price = toCents(v.price);
    const compare = v.compare_at_price ? toCents(v.compare_at_price) : 0;
    const image = p.featured_image || (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
    const url = p.url || `${ROOT}products/${p.handle}`;
    const multi = variants.length > 1;

    const select = multi
      ? `<select class="livani-upsell__select" name="id" aria-label="${esc(p.title)} options">${variants
          .map((vv) => {
            const vp = toCents(vv.price);
            const vc = vv.compare_at_price ? toCents(vv.compare_at_price) : 0;
            return `<option value="${vv.id}" data-price="${esc(money(vp))}" data-compare="${vc > vp ? esc(money(vc)) : ''}">${esc(vv.title)}</option>`;
          })
          .join('')}</select>`
      : '';

    return `<div class="livani-upsell__slide" data-upsell-slide data-product-id="${p.id}" role="group" aria-roledescription="slide" aria-label="${index}">
      <a class="livani-upsell__media" href="${esc(url)}" tabindex="-1">${image ? `<img src="${esc(sized(image, 200))}" alt="${esc(p.title)}" width="80" height="80" loading="lazy">` : ''}</a>
      <div class="livani-upsell__info">
        <a class="livani-upsell__title" href="${esc(url)}">${esc(p.title)}</a>
        <div class="livani-upsell__prices">
          <span class="livani-upsell__price" data-upsell-price>${esc(money(price))}</span>
          <s class="livani-upsell__compare" data-upsell-compare${compare > price ? '' : ' hidden'}>${compare > price ? esc(money(compare)) : ''}</s>
        </div>
        ${select}
      </div>
      <button type="button" class="livani-upsell__add" data-upsell-add data-variant-id="${v.id}" aria-label="Add ${esc(p.title)} to cart">${CART_ICON}</button>
    </div>`;
  }

  /** Arrows and dots follow the number of slides. */
  syncControls() {
    const count = this.slides.length;
    this.querySelectorAll('[data-upsell-prev], [data-upsell-next]').forEach((b) => (b.hidden = count < 2));
    const dots = this.querySelector('[data-upsell-dots]');
    if (dots && dots.children.length !== (count > 1 ? count : 0)) {
      dots.innerHTML =
        count > 1
          ? Array.from({ length: count }, (_, i) => `<button type="button" class="livani-upsell__dot" data-upsell-dot data-index="${i}" aria-label="Show product ${i + 1}" aria-current="${i === 0}"></button>`).join('')
          : '';
    }
    this.updateDots();
  }

  currentIndex() {
    const track = this.track;
    if (!track || !track.clientWidth) return 0;
    return Math.round(track.scrollLeft / track.clientWidth);
  }

  go(index) {
    const track = this.track;
    const count = this.slides.length;
    if (!track || !count) return;
    const target = ((index % count) + count) % count;
    track.scrollTo({ left: target * track.clientWidth, behavior: 'smooth' });
    this.updateDots(target);
  }

  updateDots(index = this.currentIndex()) {
    this.querySelectorAll('[data-upsell-dot]').forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
  }

  onScroll = (event) => {
    if (event.target !== this.track) return;
    cancelAnimationFrame(this._raf);
    this._raf = requestAnimationFrame(() => this.updateDots());
  };

  onChange = (event) => {
    const select = event.target instanceof Element ? event.target.closest('select[name="id"]') : null;
    if (!select) return;
    const slide = select.closest('[data-upsell-slide]');
    const option = select.selectedOptions[0];
    if (!slide || !option) return;
    const price = slide.querySelector('[data-upsell-price]');
    const compare = slide.querySelector('[data-upsell-compare]');
    if (price) price.textContent = option.dataset.price || '';
    if (compare) {
      compare.textContent = option.dataset.compare || '';
      compare.hidden = !option.dataset.compare;
    }
  };

  onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('[data-upsell-prev]')) return this.go(this.currentIndex() - 1);
    if (target.closest('[data-upsell-next]')) return this.go(this.currentIndex() + 1);
    const dot = target.closest('[data-upsell-dot]');
    if (dot) return this.go(Number(dot.dataset.index) || 0);
    const add = target.closest('[data-upsell-add]');
    if (add) this.addToCart(add);
  };

  async addToCart(button) {
    if (button.disabled) return;
    const slide = button.closest('[data-upsell-slide]');
    const select = slide ? slide.querySelector('select[name="id"]') : null;
    const variantId = select ? select.value : button.dataset.variantId;
    if (!variantId) return;

    const errorEl = this.querySelector('[data-upsell-error]');
    if (errorEl) errorEl.hidden = true;
    button.disabled = true;
    button.classList.add('is-loading');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${ROOT}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      const cart = await (await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' } })).json();

      if (data.status) {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, itemCount: cart.item_count, source: 'livani-cart-upsell' },
        });
        this.dispatchEvent(new CartErrorEvent({ error: data.message || 'Add to cart failed', code: 'INVALID' }));
        if (errorEl) {
          errorEl.textContent = data.description || data.message || 'Sorry, that item could not be added.';
          errorEl.hidden = false;
        }
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: cart.item_count, sections: data.sections, source: 'livani-cart-upsell', didError: false },
      });
    } catch (error) {
      console.error('[livani-cart-upsell]', error);
      deferred.reject(error);
      if (errorEl) {
        errorEl.textContent = 'Sorry, something went wrong. Please try again.';
        errorEl.hidden = false;
      }
    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }
  }
}

if (!customElements.get('livani-cart-upsell')) {
  customElements.define('livani-cart-upsell', LivaniCartUpsell);
}